import React, { type ReactElement, useMemo, useState } from 'react';
import { autoPlacement, autoUpdate, type Middleware, offset, Placement, shift, useFloating } from '@floating-ui/react';

import PopOverTrigger from '../PopOverTrigger';
import PopOverContent from '../PopOverContent';

export type TUsePopOverProps = {
  children: ReactElement<HTMLDivElement>[];
  /** Displays the popover when hover the trigger. */
  openOnHover?: boolean;
  /** Displays the popover when clicking the trigger. */
  openOnClick?: boolean;
  /** Displays the popover immediately after render. */
  openOnLoad?: boolean;
  /** Defaults to `PLACEMENT.RIGHT`. Possible values are `PLACEMENT.RIGHT` `PLACEMENT.LEFT` `PLACEMENT.TOP`
   * `PLACEMENT.BOTTOM`. */
  placement?: Placement;
  /** The popover is offset 20px along the reference. */
  skidding?: number;
  /** The popover is offset 20px away from the reference. */
  distance?: number;
  /** Auto placement to avoid clipping or overflow. */
  isAutoPlacement?: boolean;
  /** Auto shift to avoid clipping or overflow. cannot be used in conjunction with `isAutoPlacement`. */
  isAutoShift?: boolean;
};

const usePopOver = ({
  children,
  placement,
  skidding,
  distance,
  openOnClick,
  openOnHover,
  openOnLoad = false,
  isAutoPlacement = false,
  isAutoShift = false,
}: TUsePopOverProps) => {
  const TargetTrigger = children.find(({ type }) => type === PopOverTrigger) as ReactElement;
  const TargetContent = children.find(({ type }) => type === PopOverContent) as ReactElement<{
    onClick?: (event: unknown) => void;
    onClose?: (event: unknown) => void;
  }>;
  const [isOpen, setIsOpen] = useState(openOnLoad);
  const middleware: Middleware[] = [
    offset({
      mainAxis: distance ?? 0,
      crossAxis: skidding ?? 0,
    }),
  ];

  if (isAutoPlacement) {
    middleware.push(autoPlacement());
  } else if (isAutoShift) {
    middleware.push(shift());
  }

  const {
    refs,
    floatingStyles,
    placement: resolvedPlacement,
  } = useFloating({
    strategy: 'absolute',
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  });
  const styles = useMemo(
    () => ({
      popper: floatingStyles,
    }),
    [floatingStyles],
  );
  const attributes = useMemo(
    () => ({
      popper: {
        'data-popper-placement': resolvedPlacement,
      },
    }),
    [resolvedPlacement],
  );
  const triggerProps = {
    placement,
    onClick: (event) => {
      if (!openOnClick) {
        return;
      }

      setIsOpen(!isOpen);

      if (typeof TargetContent?.props?.onClick === 'function') {
        TargetContent.props.onClick(event);
      }
    },
    onBlur: (event) => {
      if (!openOnClick) {
        return;
      }

      setIsOpen(false);

      if (typeof TargetContent?.props?.onClose === 'function') {
        TargetContent.props.onClose(event);
      }
    },
    onMouseOver: () => {
      if (!openOnHover) {
        return;
      }

      setIsOpen(true);
    },
    onMouseOut: () => {
      if (!openOnHover) {
        return;
      }

      setIsOpen(false);
    },
  };
  const contentProps = {
    onClose: (event) => {
      setIsOpen(false);

      if (typeof TargetContent?.props?.onClose === 'function') {
        TargetContent.props.onClose(event);
      }
    },
  };

  return {
    attributes,
    contentProps,
    isOpen,
    setPopperElement: refs.setFloating,
    setReferenceElement: refs.setReference,
    styles,
    Content: React.cloneElement(TargetContent, contentProps),
    Trigger: React.cloneElement(TargetTrigger, triggerProps),
  };
};

export default usePopOver;
